import { useTranslation } from 'react-i18next';
import { BookOpenCheck, Users, Workflow } from 'lucide-react';
import { StatCard, Badge } from '@bliks-io/ui';
import type { DashboardStats } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { formatFrequency } from '@/lib/formatFrequency';

interface DashboardStatCardsProps {
  stats: DashboardStats;
}

export function DashboardStatCards({ stats }: DashboardStatCardsProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={<Workflow className="h-5 w-5 text-purple-600" />}
        iconClassName="bg-purple-50"
        title={t('dashboard.stats.activeProcesses')}
        value={stats.activeProcessCount}
        description={t('dashboard.stats.activeProcessesDesc', { count: stats.activeProcessCount })}
      />
      <StatCard
        icon={<BookOpenCheck className="h-5 w-5 text-emerald-600" />}
        iconClassName="bg-emerald-50"
        title={t('dashboard.stats.totalActivities', { count: stats.totalActivityCount })}
        value={stats.totalActivityCount}
        description=""
      >
        {stats.topActivities.length > 0 && (
          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
            {stats.topActivities.map((a) => (
              <div key={a.name} className="flex justify-between gap-2">
                <span className="truncate">{a.name}</span>
                <span className="shrink-0 font-medium">
                  {a.frequency ? formatFrequency(a.frequency, a.frequencyTimesPerPeriod, t) : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </StatCard>
      <StatCard
        icon={<ENTITY_ICONS.system className="h-5 w-5 text-orange-600" />}
        iconClassName="bg-orange-50"
        title={t('dashboard.stats.usedSystems')}
        value={stats.usedSystemCount}
        description={t('dashboard.stats.usedSystemsDesc', { count: stats.usedSystemCount })}
      >
        {stats.topSystems.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {stats.topSystems.map((sys) => (
              <Badge key={sys.name} variant="outline" size="sm">
                {sys.name} ({t('dashboard.hoursSuffix', { value: sys.usageHours })})
              </Badge>
            ))}
          </div>
        )}
      </StatCard>
      <StatCard
        icon={<Users className="h-5 w-5 text-teal-600" />}
        iconClassName="bg-teal-50"
        title={t('dashboard.stats.networkInteractions')}
        value={stats.networkInteractionCount}
        description={t('dashboard.stats.networkDesc', { count: stats.networkInteractionCount })}
      >
        <div className="mt-2 flex gap-2">
          <Badge colorScheme="success" size="sm">
            {stats.directContactCount} {t('dashboard.stats.direct')}
          </Badge>
          <Badge variant="outline" size="sm">
            {stats.indirectContactCount} {t('dashboard.stats.indirect')}
          </Badge>
        </div>
      </StatCard>
    </div>
  );
}
